import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
// ^ no longer needed, calls happen in PostCard
import PostCard from "./PostCard";
import {
  fetchInfiniteFeed,
  getOrCreateUserId,
  getFollowedProfiles,
} from "../api";
import useIdleAutoScroll from "../hooks/useIdleAutoScroll";

function PostList({
  initialPosts = [],
  onPostUpdated,
  sharedPostId,
  isAdmin,
  useInfiniteScroll = false,
  initialLoading = false,
}) {
  const [posts, setPosts] = useState(initialPosts);
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const userId = getOrCreateUserId();
  const hasInitialPosts = useRef(false);

  // starts the idle timer as soon as PostList is mounted
  // useIdleAutoScroll({ enabled: useInfiniteScroll });

  // Split posts into shared and recent
  const sharedPost = posts.find((post) => post.id === sharedPostId);
  const recentPosts = posts.filter((post) => post.id !== sharedPostId);

  const loadMorePosts = async () => {
    if (loadingRef.current || !hasMore || !hasInitialPosts.current) return;
    loadingRef.current = true;
    setIsLoading(true);
    try {
      const seenIds = posts.map((post) => post.id);
      const newPosts = await fetchInfiniteFeed(
        seenIds,
        25,
        getFollowedProfiles()
      );

      if (newPosts.length === 0) {
        setHasMore(false);
      } else {
        // Transform posts to include likeCount and userHasLiked
        const transformed = newPosts.map((p) => ({
          ...p,
          likeCount:
            p.likeCount !== undefined ? p.likeCount : p.Likes?.length || 0,
          userHasLiked:
            p.userHasLiked !== undefined
              ? p.userHasLiked
              : p.Likes?.some((l) => l.userId === userId) || false,
        }));

        setPosts((prev) => [...prev, ...transformed]);
      }
    } catch (err) {
      console.error("Error loading more posts:", err);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  };

  /**
   * Only merge the *changed* items coming from <App/> instead of dumping the
   * whole array – this preserves the identity of untouched cards so React
   * doesn't remount them (and, by extension, doesn't reload any embedded
   * iframes/canvases).
   */
  useEffect(() => {
    if (!initialPosts.length) return;

    setPosts((prev) => {
      /* different length or order ⇒ hard replace (refresh / pagination) */
      const orderChanged =
        prev.length !== initialPosts.length ||
        !prev.every((p, i) => p.id === initialPosts[i]?.id);
      if (orderChanged) return initialPosts;

      /* otherwise merge likeCount / userHasLiked / comments deltas only */
      let changed = false;
      const merged = prev.map((p) => {
        const incoming = initialPosts.find((q) => q.id === p.id);
        if (
          incoming &&
          (incoming.likeCount !== p.likeCount ||
            incoming.userHasLiked !== p.userHasLiked ||
            (incoming.comments?.length || 0) !== (p.comments?.length || 0))
        ) {
          changed = true;
          return {
            ...p,
            likeCount: incoming.likeCount,
            userHasLiked: incoming.userHasLiked,
            comments: incoming.comments, // <<< keep them
          };
        }
        return p; // keep reference
      });
      return changed ? merged : prev;
    });

    hasInitialPosts.current = initialPosts.length > 0;
  }, [initialPosts]);

  // Only set up the intersection observer if useInfiniteScroll is true
  useEffect(() => {
    if (!useInfiniteScroll) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [useInfiniteScroll, posts.length]); // Add posts.length to dependencies to ensure observer is updated when posts change

  /** Replace one post in-place (by id) wherever it currently sits.   */
  const updatePostInList = (updated) => {
    setPosts((prev) => {
      const idx = prev.findIndex((p) => p.id === updated.id);
      if (idx === -1) return prev; // not in this slice

      const current = prev[idx];
      // Check if anything actually changed to avoid unnecessary re-renders
      const hasChanges =
        current.likeCount !== updated.likeCount ||
        current.userHasLiked !== updated.userHasLiked ||
        (current.comments?.length || 0) !== (updated.comments?.length || 0);

      if (!hasChanges) return prev; // no changes, keep current state

      // Safety guard: prevent missing fields from clobbering valid data
      const safe = {
        ...updated,
        likeCount: updated.likeCount ?? current.likeCount,
        userHasLiked: updated.userHasLiked ?? current.userHasLiked,
      };

      const next = [...prev];
      next[idx] = { ...next[idx], ...safe };
      return next;
    });

    // also notify parent (keeps initialPosts in sync for page-1 items)
    onPostUpdated?.(updated);
  };

  return (
    <Box
      sx={{
        m: 0,
        p: 0,
      }}
    >
      {sharedPost && (
        <PostCard
          key={sharedPost.id}
          post={sharedPost}
          onPostUpdated={updatePostInList}
          isShared={true}
          isAdmin={isAdmin}
        />
      )}

      {sharedPost && recentPosts.length > 0 && (
        <Typography
          variant="h6"
          sx={{ mb: 3, mt: 4, color: "text.primary", fontWeight: "bold" }}
        >
          More Posts
        </Typography>
      )}

      {recentPosts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          onPostUpdated={updatePostInList}
          isShared={false}
          isAdmin={isAdmin}
        />
      ))}

      {/* Show spinner while the very first load is in flight              */}
      {posts.length === 0 &&
        (initialLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Typography sx={{ color: "text.secondary" }}>
            No posts yet. Start by creating one!
          </Typography>
        ))}

      {/* Loading indicator and intersection observer target - only show if using infinite scroll */}
      {useInfiniteScroll && (
        <Box
          ref={loaderRef}
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 2,
            visibility: hasMore ? "visible" : "hidden",
            minHeight: "50px", // Ensure there's enough height for the observer to detect
          }}
        >
          {isLoading && <CircularProgress size={24} />}
        </Box>
      )}
    </Box>
  );
}

export default PostList;
